import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import classNames from 'classnames'
import * as _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import { QuestionOption } from '../types'
import CheckRadio from '../../OldPortalComponents/CheckRadio'
import Checkbox from '../../OldPortalComponents/Checkbox'
import TextInput from '../../OldPortalComponents/TextInput'
import IconLabelButton from '../../OldPortalComponents/IconLabelButton'
import { HelperService } from '@/services/helper.service'
import { getMcqDistractors } from '@/store/quizzes/actions'

interface MultipleChoiceQuestionProps {
  questionType: 'radio' | 'checkbox'
  questionText: string
  questionOptions: QuestionOption[]
  onOptionsChange: (options: QuestionOption[]) => void
}

const MultipleChoiceQuestion: React.FC<MultipleChoiceQuestionProps> = ({
  questionType,
  questionText,
  questionOptions,
  onOptionsChange,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const helper = new HelperService()
  const [generatingDistractors, setGeneratingDistractors] = useState(false)
  const [isBtnDisable, setIsBtnDisable] = useState(false)

  useEffect(() => {
    setIsBtnDisable(
      !questionOptions.some(
        opt => opt.is_correct && opt.text.trim().length > 0,
      ),
    )
  }, [questionOptions])

  const handleOptionChange = (value: string, optionIndex: number) => {
    const temp = [...questionOptions]
    temp[optionIndex] = {
      ...temp[optionIndex],
      text: value,
    }
    onOptionsChange(temp)
  }

  const handleCheckboxCorrect = (isChecked: boolean, optionIndex: number) => {
    const temp = [...questionOptions]
    temp[optionIndex].is_correct = isChecked
    onOptionsChange(temp)
  }

  const handleRadioCorrect = (value: string) => {
    const temp = [...questionOptions]
    temp.forEach(option => {
      option.is_correct = false
    })
    temp[parseInt(value)].is_correct = true
    onOptionsChange(temp)
  }

  const handleOptionDelete = (optionId: string) => {
    const filtered = questionOptions.filter(option => option.id !== optionId)
    onOptionsChange(filtered)
  }

  const addOption = () => {
    const temp = [...questionOptions]
    temp.push({
      id: uuidv4(),
      text: '',
      is_correct: false,
    })
    onOptionsChange(temp)
  }

  const getDistractors = async () => {
    try {
      let temp = questionOptions.filter(e => e.text.trim().length !== 0)
      const rawText = questionText.replace(/(<([^>]+)>)/gi, '')

      if (!rawText) {
        helper.showToaster(
          t('Please input question before generating distractors'),
        )
        return
      }

      setGeneratingDistractors(true)
      const data = {
        text: rawText.trim(),
        correct_options: temp.filter(opt => opt.is_correct).map(e => e.text),
      }

      const response: any = await dispatch(getMcqDistractors(data))
      if (response && response.distractors.length > 0) {
        response.distractors.forEach((e: string) =>
          temp.push({
            id: uuidv4(),
            text: e,
            is_correct: false,
          }),
        )
      }

      if (temp && temp.length !== 0) {
        temp = _.uniqBy(
          _.sortBy(temp, e => !e.is_correct),
          'text',
        )
        temp = _.shuffle(temp)
      }

      onOptionsChange([...temp])
      setGeneratingDistractors(false)
    } catch (error) {
      setGeneratingDistractors(false)
    }
  }

  return (
    <div className="options-editor">
      <h4 className="card-subheading">{t('Options')}</h4>
      <div className="flex-container">
        <div className="page-label">{t('Is correct')}</div>
        <div className="page-label flex-one">{t('Enter options below')}</div>
        {questionType === 'radio' && (
          <button
            className={classNames({
              'btn page-label btn-link': true,
            })}
            disabled={isBtnDisable}
            onClick={getDistractors}
          >
            {t('Generate Distractors')}
          </button>
        )}
      </div>
      {questionOptions.map((option, index) => (
        <div key={option.id + '_option_' + index}>
          <div className="flex-container">
            <div className="options-editor-checkbox">
              {questionType === 'checkbox' && (
                <Checkbox
                  checked={option.is_correct}
                  onCheckboxChange={(
                    event: React.ChangeEvent<HTMLInputElement>,
                  ) => handleCheckboxCorrect(event.target.checked, index)}
                />
              )}
              {questionType === 'radio' && (
                <CheckRadio
                  name={questionText}
                  checked={option.is_correct}
                  onRadioChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleRadioCorrect(event.target.value)
                  }
                  value={index}
                />
              )}
            </div>
            <div className="flex-one no-padding">
              <TextInput
                inputChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleOptionChange(event.target.value, index)
                }
                value={option.text}
              />
            </div>
            <button
              onClick={() => handleOptionDelete(option.id)}
              className="options-editor-delete"
            >
              <i className="icon-delete" />
            </button>
          </div>
          <div className="clearfix" />
        </div>
      ))}
      {generatingDistractors && (
        <div className="doc-checking-msg d-flex align-items-center justify-content-center clr-black">
          <span className="doc-loader spinner-width-20 mr-2"></span>
          {t('Generating Distractors... Please wait')}
        </div>
      )}
      <IconLabelButton
        showIcon
        iconClass="icon-add"
        buttonLabel={t('ADD AN OPTION')}
        buttonClass="add-option-btn"
        onClick={addOption}
      />
    </div>
  )
}

export default MultipleChoiceQuestion
